import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { registrationService } from "../firebase/services";
import { isFirebaseConfigured } from "../firebase/config";
import {
  ShieldAlert,
  Upload,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileText,
  User,
  GraduationCap,
  Sparkles,
} from "lucide-react";

export const RegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    city: "Bhilai",
    state: "Chhattisgarh",
    college: "",
    course: "",
    branch: "",
    yearSemester: "",
    graduationYear: "",
    experience: "Beginner",
    interestedArea: "Cyber Security",
    statementOfPurpose: "",
    hearAbout: "",
    attendRegularly: false,
    confirmCorrect: false,
    agreeTerms: false,
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Please upload a valid image file (JPEG or PNG).");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError("Passport photo size must be less than 5MB.");
        return;
      }
      setError("");
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.fullName.trim()) return setError("Please enter your Full Name.");
    if (!formData.email.trim() || !formData.email.includes("@"))
      return setError("Please enter a valid Email Address.");
    if (!formData.mobile.trim() || formData.mobile.length < 10)
      return setError("Please enter a valid 10-digit Mobile Number.");
    if (!photoFile)
      return setError("Please upload your passport-size photo for student ID verification.");
    if (!formData.college.trim()) return setError("Please enter your College / Institute name.");
    if (!formData.course.trim()) return setError("Please enter your Course / Degree.");
    if (!formData.yearSemester.trim())
      return setError("Please specify your Current Year / Semester.");
    if (!formData.statementOfPurpose.trim())
      return setError("Please share why you want to join this Masterclass.");
    if (!formData.attendRegularly)
      return setError("Please confirm you are willing to attend the 15-day Masterclass regularly.");
    if (!formData.confirmCorrect)
      return setError("Please confirm that the information provided is correct.");
    if (!formData.agreeTerms)
      return setError("Please accept the terms and conditions of the Masterclass.");

    setLoading(true);

    try {
      if (!isFirebaseConfigured) {
        throw new Error(
          "Firebase is not configured yet. Please enter your Firebase config keys in frontend/.env to complete real registration."
        );
      }

      const result = await registrationService.submitRegistration(formData, photoFile);
      
      sessionStorage.setItem(
        "kt_last_reg",
        JSON.stringify({
          applicationNumber: result.applicationNumber,
          fullName: formData.fullName,
          email: formData.email,
          activationToken: result.activationToken,
        })
      );

      navigate("/registration-success");
    } catch (err) {
      console.error("Registration error:", err);
      setError(err.message || "Failed to submit registration. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 text-xs font-mono">
            <span>Kian Technologies • Bhilai</span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight">
            Masterclass Student Registration
          </h1>
          <p className="text-sm text-muted-foreground font-mono">
            15 Days Free Cyber Security Masterclass • Verification & Access Form
          </p>
          <p className="text-[11px] text-muted-foreground mt-2">Tuition is free. A one-time registration/administrative fee of ₹599 applies to confirmed registration.</p>
        </div>

        {error && (
          <div
            data-testid="registration-error-alert"
            className="mb-6 p-4 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm flex items-start gap-3"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <span className="font-semibold">Submission Error: </span>
              {error}
            </div>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="bg-card border border-border rounded-xl shadow-lg p-6 sm:p-8 space-y-8"
        >
          {/* Section 1: Personal Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 border-b border-border pb-2 text-foreground font-semibold text-base">
              <User className="w-4 h-4 text-emerald-400" />
              <span>1. Personal Details</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  Full Name <span className="text-emerald-500">*</span>
                </label>
                <input
                  type="text"
                  name="fullName"
                  data-testid="reg-fullname-input"
                  required
                  placeholder="e.g. Rahul Sharma"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  Email Address <span className="text-emerald-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  data-testid="reg-email-input"
                  required
                  placeholder="name@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  Mobile Number (WhatsApp) <span className="text-emerald-500">*</span>
                </label>
                <input
                  type="tel"
                  name="mobile"
                  data-testid="reg-mobile-input"
                  required
                  placeholder="10-digit mobile number"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  City <span className="text-emerald-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  data-testid="reg-city-input"
                  required
                  placeholder="e.g. Bhilai, Durg, Raipur"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  State
                </label>
                <input
                  type="text"
                  name="state"
                  data-testid="reg-state-input"
                  placeholder="e.g. Chhattisgarh"
                  value={formData.state}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              {/* Photo Upload */}
              <div className="sm:col-span-2">
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  Passport-size Photo <span className="text-emerald-500">*</span>
                  <span className="text-muted-foreground font-normal ml-1">
                    (Used on verified Student ID card upon approval, max 5MB)
                  </span>
                </label>
                <div className="flex flex-col sm:flex-row items-center gap-4 p-4 border border-dashed border-border rounded-lg bg-muted/20">
                  {photoPreview ? (
                    <img
                      src={photoPreview}
                      alt="Passport preview"
                      className="w-20 h-24 object-cover rounded-md border border-border shadow-sm"
                    />
                  ) : (
                    <div className="w-20 h-24 rounded-md bg-muted flex items-center justify-center text-muted-foreground border border-border">
                      <User className="w-8 h-8 opacity-40" />
                    </div>
                  )}
                  <div className="flex-1 text-center sm:text-left space-y-2">
                    <input
                      type="file"
                      id="passport-upload"
                      accept="image/png, image/jpeg, image/jpg"
                      data-testid="reg-photo-input"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="passport-upload"
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-muted text-xs font-mono cursor-pointer transition-colors"
                    >
                      <Upload className="w-3.5 h-3.5 text-emerald-400" />
                      <span>{photoFile ? "Change Photo" : "Upload Passport Photo"}</span>
                    </label>
                    <p className="text-[11px] text-muted-foreground">
                      {photoFile ? photoFile.name : "JPEG, JPG, or PNG format with clear face view."}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Education Details */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 border-b border-border pb-2 text-foreground font-semibold text-base">
              <GraduationCap className="w-4 h-4 text-cyan-400" />
              <span>2. Education Details</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  College / Institute Name <span className="text-emerald-500">*</span>
                </label>
                <input
                  type="text"
                  name="college"
                  data-testid="reg-college-input"
                  required
                  placeholder="e.g. BIT Durg, Rungta College, CSVTU, SSTC Bhilai"
                  value={formData.college}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  Course / Degree <span className="text-emerald-500">*</span>
                </label>
                <input
                  type="text"
                  name="course"
                  data-testid="reg-course-input"
                  required
                  placeholder="e.g. B.Tech, BCA, MCA, B.Sc, Diploma"
                  value={formData.course}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  Branch / Stream
                </label>
                <input
                  type="text"
                  name="branch"
                  data-testid="reg-branch-input"
                  placeholder="e.g. CSE, IT, Cyber Security, ECE, AI/DS"
                  value={formData.branch}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  Current Year / Semester <span className="text-emerald-500">*</span>
                </label>
                <input
                  type="text"
                  name="yearSemester"
                  data-testid="reg-year-input"
                  required
                  placeholder="e.g. 2nd Year / 4th Sem"
                  value={formData.yearSemester}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  Expected Graduation Year
                </label>
                <input
                  type="text"
                  name="graduationYear"
                  data-testid="reg-gradyear-input"
                  placeholder="e.g. 2026, 2027"
                  value={formData.graduationYear}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Cyber Security Background */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 border-b border-border pb-2 text-foreground font-semibold text-base">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span>3. Cyber Security Details</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  Previous Cyber Security Experience <span className="text-emerald-500">*</span>
                </label>
                <select
                  name="experience"
                  data-testid="reg-experience-select"
                  value={formData.experience}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="Beginner">Beginner (No prior background)</option>
                  <option value="Basic Knowledge">Basic Knowledge (Networking / Linux)</option>
                  <option value="Intermediate">Intermediate (Used tools / CTFs)</option>
                  <option value="Experienced">Experienced (Bug hunting / Practicing)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  Primary Area of Interest <span className="text-emerald-500">*</span>
                </label>
                <select
                  name="interestedArea"
                  data-testid="reg-interest-select"
                  value={formData.interestedArea}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="Cyber Security">Cyber Security</option>
                  <option value="Ethical Hacking">Ethical Hacking</option>
                  <option value="Networking">Networking & Protocols</option>
                  <option value="Web Security">Web Application Security</option>
                  <option value="Digital Forensics">Digital Forensics (DFIR)</option>
                  <option value="SOC / Blue Team">SOC / Blue Team Defense</option>
                  <option value="Penetration Testing">Penetration Testing (VAPT)</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  Why do you want to join this 15 Days Masterclass? <span className="text-emerald-500">*</span>
                </label>
                <textarea
                  name="statementOfPurpose"
                  data-testid="reg-sop-textarea"
                  required
                  rows={3}
                  placeholder="Share your learning goals, interest in cyber defense, or career ambitions..."
                  value={formData.statementOfPurpose}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-mono font-medium text-foreground mb-1">
                  How did you hear about Kian Technologies?
                </label>
                <input
                  type="text"
                  name="hearAbout"
                  data-testid="reg-hearabout-input"
                  placeholder="e.g. College WhatsApp group, Faculty, LinkedIn, Friend"
                  value={formData.hearAbout}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 text-sm rounded-lg bg-background border border-input focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Declarations */}
          <div className="space-y-4 pt-2 border-t border-border">
            <div className="space-y-3 text-sm">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  name="attendRegularly"
                  data-testid="reg-attend-checkbox"
                  checked={formData.attendRegularly}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 rounded border-input text-emerald-500 focus:ring-emerald-400"
                />
                <span className="text-xs text-foreground">
                  I confirm that I am willing and committed to attend the <strong>15-day Masterclass regularly</strong> to complete all practical labs and assessments. *
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  name="confirmCorrect"
                  data-testid="reg-confirm-checkbox"
                  checked={formData.confirmCorrect}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 rounded border-input text-emerald-500 focus:ring-emerald-400"
                />
                <span className="text-xs text-foreground">
                  I confirm that all personal, contact, college, and academic information provided by me is correct. *
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  name="agreeTerms"
                  data-testid="reg-terms-checkbox"
                  checked={formData.agreeTerms}
                  onChange={handleInputChange}
                  className="mt-1 h-4 w-4 rounded border-input text-emerald-500 focus:ring-emerald-400"
                />
                <span className="text-xs text-foreground">
                  I agree to the <Link to="/terms" className="text-emerald-400 hover:underline">Terms & Conditions</Link> and <Link to="/privacy-policy" className="text-emerald-400 hover:underline">Privacy Policy</Link> for the 15-day Cyber Security Masterclass by Kian Technologies. *
                </span>
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-[11px] text-muted-foreground font-mono">
              Submission triggers real manual verification by Kian Tech instructors.
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="reg-submit-btn"
              className="w-full sm:w-auto px-8 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-mono text-sm font-semibold shadow-md transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Submitting Application...</span>
                </>
              ) : (
                <span>Submit Registration</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
